import { useCallback, useState, useEffect, useRef } from 'react'

import { useLogger } from '@vp/ubik-context'

import { useAppState } from '../../components/root/contexts/AppContext'
import { useTenant } from '../../components/root/contexts/TenantContext'
import { useProjects } from '../../components/root/contexts/ProjectsContext'
import { useWorksDataContext } from '../../components/root/contexts/WorksDataContext'
import { useCart } from '../useCart'
import { useAlertState } from '../../components/root/contexts/AlertContext'
import { useLocalizations } from '@vp/ubik-localizations-provider'
import { useTracking } from '../../components/root/contexts/TrackingContext'
import { TrackingLabel } from '../../components/root/contexts/TrackingContext/TrackingConstants'
import { executeAddToCart } from './executeAddToCart'
import type { AddToCartAction, AddToCartDependencies } from './types'
import { useAuthorization } from '../../components/root/contexts/AuthorizationProvider'

/**
 * Custom hook for add to cart functionality
 * Acts primarily as a dependency aggregator and state manager
 * Business logic is delegated to pure functions for better testability
 *
 * @returns Object containing the add to cart handler and loading state
 */
export const useAddToCartAction = (): AddToCartAction => {
  const { isCareAgent, culture, nextStepUrl, primaryProject } = useAppState()
  const { authorizationHeader } = useAuthorization()
  const { cartTenant } = useTenant()
  const { workIdsWithValidQuantities, isWorkQuantitiesValid, workIdsWithInvalidQuantities, worksData } = useWorksDataContext()
  const { projects } = useProjects()
  const { trackAddToCart, trackMatchingProductAddToCartError } = useTracking()
  const { t } = useLocalizations()
  const { setError, setSuccess, setProjectQtyErrors } = useAlertState()
  const [isUpdatingCart, setIsUpdatingCart] = useState(false)
  const { upsertCartAsync, upsertCartMultiWorkAsync, verifyCartContainsWorkIds } = useCart()
  const logger = useLogger()
  const abortControllerRef = useRef<AbortController | null>(null)

  // Cleanup abort controller on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        logger.info('Aborting pending add-to-cart request due to component unmount')
        abortControllerRef.current.abort()
      }
    }
  }, [])

  const onAddToCartClick = useCallback(async () => {
    // Abort any pending request
    if (abortControllerRef.current) {
      logger.info('Aborting pending add-to-cart request due to new request')
      abortControllerRef.current.abort()
    }

    // Create new abort controller for this request
    abortControllerRef.current = new AbortController()

    if (!isWorkQuantitiesValid) {
      setProjectQtyErrors(workIdsWithInvalidQuantities)

      // Track error only for related products (non-primary projects) with invalid quantities
      workIdsWithInvalidQuantities.forEach((workId) => {
        const project = projects.find((p) => p.workId === workId)
        const isRelatedProduct = project && primaryProject && project.workId !== primaryProject.workId

        if (isRelatedProduct) {
          trackMatchingProductAddToCartError(
            TrackingLabel.ADD_TO_CART_INVALID_QUANTITY_ERROR,
            project.mpvId,
            project.productName,
            project.productKey,
            project.productVersion,
          )
        }
      })
    } else {
      // Aggregate all dependencies for the business logic
      const dependencies: AddToCartDependencies = {
        authorizationHeader,
        cartTenant,
        isCareAgent,
        culture,
        nextStepUrl,
        projects: structuredClone(projects),
        worksData: structuredClone(worksData),
        workIdsWithValidQuantities,
        workIdsWithInvalidQuantities,
        isWorkQuantitiesValid,
        upsertCartAsync,
        upsertCartMultiWorkAsync,
        verifyCartContainsWorkIds,
        trackAddToCart,
        trackMatchingProductAddToCartError,
        setError,
        setSuccess,
        setProjectQtyErrors,
        t,
        logger,
      }

      try {
        await executeAddToCart(dependencies, setIsUpdatingCart)
      } catch (error) {
        // Don't log errors if the request was aborted
        if (error instanceof Error && error.name !== 'AbortError') {
          throw error
        }
      }
    }
  }, [
    authorizationHeader,
    cartTenant,
    isCareAgent,
    culture,
    nextStepUrl,
    JSON.stringify(projects),
    JSON.stringify(worksData),
    workIdsWithValidQuantities,
    workIdsWithInvalidQuantities,
    isWorkQuantitiesValid,
    upsertCartAsync,
    upsertCartMultiWorkAsync,
    trackAddToCart,
    trackMatchingProductAddToCartError,
    setError,
    setSuccess,
    setProjectQtyErrors,
    t,
    logger,
    primaryProject,
  ])

  return {
    onAddToCartClick,
    isUpdatingCart,
  }
}
